import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';

const ResizableHeader = ({ 
  children, 
  onResize, 
  minWidth = 50, 
  maxWidth = 500,
  initialWidth = 150,
  columnIndex 
}) => {
  const [width, setWidth] = useState(initialWidth);
  const [isResizing, setIsResizing] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(0);
  const headerRef = useRef(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;

      const deltaX = e.clientX - startX;
      const newWidth = Math.max(minWidth, Math.min(maxWidth, startWidth + deltaX));
      
      setWidth(newWidth);
      onResize(columnIndex, newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, startX, startWidth, minWidth, maxWidth, onResize, columnIndex]);

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
    setStartX(e.clientX);
    setStartWidth(width);
  };

  return (
    <Box
      ref={headerRef}
      sx={{
        position: 'relative',
        width: width,
        minWidth: width,
        maxWidth: width,
        userSelect: 'none',
        display: 'inline-block',
        '&:hover .resize-handle': {
          backgroundColor: 'primary.main',
        }
      }}
    >
      {children}
      <Box
        className="resize-handle"
        onMouseDown={handleMouseDown}
        sx={{
          position: 'absolute',
          right: -2,
          top: 0,
          bottom: 0,
          width: 4,
          cursor: 'col-resize',
          backgroundColor: 'transparent',
          transition: 'background-color 0.2s',
          zIndex: 1,
          '&:hover': {
            backgroundColor: 'primary.main',
          },
          '&:active': {
            backgroundColor: 'primary.dark',
          }
        }}
      />
    </Box>
  );
};

ResizableHeader.propTypes = {
  children: PropTypes.node.isRequired,
  onResize: PropTypes.func.isRequired,
  minWidth: PropTypes.number,
  maxWidth: PropTypes.number,
  initialWidth: PropTypes.number,
  columnIndex: PropTypes.number.isRequired,
};

export default ResizableHeader;
